import type {
	Action,
	ForeachAction,
	IfAction,
	Position,
	SwitchAction,
	UntilAction,
	WorkflowDefinition,
} from "@/types/workflowDefinition";
import {
	isForeachAction,
	isIfAction,
	isSwitchAction,
	isUntilAction,
} from "@/types/workflowDefinition";
import {
	DEFAULT_NODE_HEIGHT,
	DEFAULT_NODE_WIDTH,
	NODE_SPACING,
} from "./layoutConstants";

/**
 * Auto-layout helpers for action insertion and deletion.
 *
 * Implements approach **D** from the auto-layout ADR — a push-downstream
 * shift (phase 1) plus a bounded local re-space (phase 2) when the push
 * leaves residual overlap inside the affected scope.
 *
 * **Invariants:**
 *  - All operations are **scope-bounded**: they only ever read or mutate the
 *    `Record<string, Action>` they're handed. Sibling scopes are never
 *    inspected, so manual positions outside the affected scope are preserved.
 *  - Pure functions: no Zustand, no React, no DOM. This makes them trivially
 *    unit-testable and reusable from any call site (addAction, paste,
 *    duplicate, delete).
 *  - Position math respects the workflow's layout orientation, defaulting to
 *    "horizontal" for legacy workflows that don't declare one.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Direction of the workflow flow on the canvas. */
export type FlowOrientation = "horizontal" | "vertical";

/** Auto-layout mode read from `workflow.metadata.layoutMode`. */
export type LayoutMode = "auto" | "manual";

/** A node bounding box used for overlap detection. */
export type NodeBox = {
	id: string;
	x: number;
	y: number;
	width: number;
	height: number;
};

/**
 * Handle returned by {@link findScopeHandle} — the actions record that owns
 * a target action, plus the rebuild function that puts an updated copy back
 * into the workflow tree without deep-cloning unrelated branches.
 */
export type ScopeHandle = {
	/** The actions record the target action lives inside. */
	scope: Record<string, Action>;
	/** Apply an updated copy of the scope back into the full top-level actions. */
	rebuild: (updated: Record<string, Action>) => Record<string, Action>;
};

/**
 * Insertion point shape used by `addAction`. Re-declared here so this
 * module doesn't need to import the store.
 */
export type InsertionPointLike = {
	sourceNodeId: string;
	targetNodeId?: string;
};

// ---------------------------------------------------------------------------
// Position accessor
// ---------------------------------------------------------------------------

/**
 * Read an action's stored position, returning `undefined` if the action
 * has no position set. Centralises the `metadata.position` cast so every
 * call site doesn't repeat `as Position | undefined`.
 *
 * `Action.metadata` is typed as `z.record(z.unknown())` because schemas
 * use `.passthrough()` to preserve unknown backend fields. The cast is
 * safe at runtime because we own all writers (auto-layout + manual drag).
 */
export const getActionPosition = (action: Action): Position | undefined =>
	action.metadata?.position as Position | undefined;

// ---------------------------------------------------------------------------
// Insert stride
// ---------------------------------------------------------------------------

/**
 * Per-orientation shift along the flow axis when inserting a node.
 * Matches what auto-layout already reserves (`DEFAULT_NODE_WIDTH/HEIGHT + NODE_SPACING`).
 */
export const getInsertShift = (orientation: FlowOrientation): Position => {
	if (orientation === "horizontal") {
		return { x: DEFAULT_NODE_WIDTH + NODE_SPACING, y: 0 };
	}
	return { x: 0, y: DEFAULT_NODE_HEIGHT + NODE_SPACING };
};

const strideFor = (orientation: FlowOrientation): number =>
	orientation === "horizontal"
		? DEFAULT_NODE_WIDTH + NODE_SPACING
		: DEFAULT_NODE_HEIGHT + NODE_SPACING;

// ---------------------------------------------------------------------------
// Graph traversal (scope-local)
// ---------------------------------------------------------------------------

/**
 * Build the runAfter adjacency map for a single actions record (one scope).
 *
 * Returns `{ [actionName]: [namesItWaitsFor] }`. Only references to siblings
 * in the same record are kept — runAfter values pointing outside the scope
 * (which should not happen in valid workflows) are silently dropped.
 */
const buildRunAfterIndex = (
	actions: Record<string, Action>,
): Map<string, string[]> => {
	const index = new Map<string, string[]>();
	for (const [name, action] of Object.entries(actions)) {
		const ra = action.runAfter ?? {};
		const deps: string[] = [];
		for (const dep of Object.keys(ra)) {
			if (dep in actions) deps.push(dep);
		}
		index.set(name, deps);
	}
	return index;
};

/**
 * Collect every action transitively downstream of `rootName` within the same
 * scope, following `runAfter` edges forward. The root itself is **included**
 * in the returned set so callers can shift the just-rewired target as well.
 *
 * Cycles are tolerated (each node visited once). Runs in O(n) over the scope.
 *
 * @param actions  The actions record for the scope (top-level or nested).
 * @param rootName The first action to translate; usually the original target
 *   of the insertion (i.e. the node whose runAfter was just rewritten to
 *   depend on the new action).
 */
export const collectDownstreamInScope = (
	actions: Record<string, Action>,
	rootName: string,
): Set<string> => {
	const visited = new Set<string>();
	if (!(rootName in actions)) return visited;

	// Reverse adjacency: who runs AFTER each action?
	const successors = new Map<string, string[]>();
	for (const [name, action] of Object.entries(actions)) {
		const ra = action.runAfter ?? {};
		for (const dep of Object.keys(ra)) {
			if (!(dep in actions)) continue;
			const arr = successors.get(dep) ?? [];
			arr.push(name);
			successors.set(dep, arr);
		}
	}

	const queue: string[] = [rootName];
	for (let i = 0; i < queue.length; i++) {
		const name = queue[i]!;
		if (visited.has(name)) continue;
		visited.add(name);
		const next = successors.get(name) ?? [];
		for (const s of next) if (!visited.has(s)) queue.push(s);
	}

	return visited;
};

// ---------------------------------------------------------------------------
// Position mutations (immutable, structural sharing)
// ---------------------------------------------------------------------------

/**
 * Apply a position delta to every named action in `actions`, leaving the rest
 * of the record (and every nested scope) untouched. Actions without an
 * existing `metadata.position` are skipped — auto-layout will pick them up on
 * the next pass.
 *
 * Returns the **same reference** when nothing changed so callers can use
 * identity comparison to detect a no-op.
 */
export const shiftActionPositions = (
	actions: Record<string, Action>,
	names: ReadonlySet<string>,
	delta: Position,
): Record<string, Action> => {
	if (names.size === 0 || (delta.x === 0 && delta.y === 0)) return actions;

	let changed = false;
	const next: Record<string, Action> = {};
	for (const [name, action] of Object.entries(actions)) {
		if (!names.has(name)) {
			next[name] = action;
			continue;
		}
		const pos = getActionPosition(action);
		if (!pos) {
			next[name] = action;
			continue;
		}
		next[name] = {
			...action,
			metadata: {
				...action.metadata,
				position: { x: pos.x + delta.x, y: pos.y + delta.y },
			},
		};
		changed = true;
	}
	return changed ? next : actions;
};

// ---------------------------------------------------------------------------
// Overlap detection
// ---------------------------------------------------------------------------

/** Breathing room so kissing edges count as overlap. */
const PADDING = 4;

/**
 * AABB overlap test on a flat list of boxes. O(n²) but n is the size of one
 * scope (rarely > 20 in practice) so we don't bother with a spatial index.
 */
export const hasOverlap = (boxes: ReadonlyArray<NodeBox>): boolean => {
	for (let i = 0; i < boxes.length; i++) {
		const a = boxes[i];
		if (!a) continue;
		for (let j = i + 1; j < boxes.length; j++) {
			const b = boxes[j];
			if (!b) continue;
			if (
				a.x - PADDING < b.x + b.width + PADDING &&
				a.x + a.width + PADDING > b.x - PADDING &&
				a.y - PADDING < b.y + b.height + PADDING &&
				a.y + a.height + PADDING > b.y - PADDING
			) {
				return true;
			}
		}
	}
	return false;
};

/**
 * Read action positions from a scope into AABB boxes. Actions without a
 * stored position contribute nothing (no overlap can be asserted on them).
 * Width/height fall back to the layout defaults — fine for the overlap test
 * because (a) we err on the side of detecting collisions, and (b) the store
 * doesn't have access to React Flow's measured sizes anyway.
 */
export const collectScopeBoxes = (
	actions: Record<string, Action>,
	defaults: { width: number; height: number } = {
		width: DEFAULT_NODE_WIDTH,
		height: DEFAULT_NODE_HEIGHT,
	},
): NodeBox[] => {
	const boxes: NodeBox[] = [];
	for (const [name, action] of Object.entries(actions)) {
		const pos = getActionPosition(action);
		if (!pos) continue;
		boxes.push({
			id: name,
			x: pos.x,
			y: pos.y,
			width: defaults.width,
			height: defaults.height,
		});
	}
	return boxes;
};

/**
 * Read the overlap state of a scope identified by any one of its actions.
 * Phase 2 of {@link layoutAfterInsert} calls this to decide whether a local
 * re-space is needed after the Phase 1 push.
 */
export const scopeHasOverlap = (
	topLevelActions: Record<string, Action>,
	memberActionName: string,
): boolean => {
	const handle = findScopeHandle(topLevelActions, memberActionName);
	if (!handle) return false;
	return hasOverlap(collectScopeBoxes(handle.scope));
};

// ---------------------------------------------------------------------------
// Layout mode (manual opt-out)
// ---------------------------------------------------------------------------

/**
 * Resolve the auto-layout mode from a workflow definition.
 *
 * `workflow.metadata.layoutMode = "manual"` opts out of every auto-layout
 * step; the workflow then behaves exactly like Otto did before this feature
 * shipped. Anything else (including missing metadata) is treated as `"auto"`.
 */
export const getLayoutMode = (
	workflow: WorkflowDefinition | null | undefined,
): LayoutMode =>
	workflow?.metadata?.layoutMode === "manual" ? "manual" : "auto";

// ---------------------------------------------------------------------------
// Scope lookup (returns a lens, never deep-clones outer branches)
// ---------------------------------------------------------------------------

/**
 * A nested actions record on a control-flow parent plus the function that
 * folds an updated copy of that record back into the parent. Used by
 * {@link findScopeHandle} so the tree walk is uniform across If / ForEach /
 * Until / Switch instead of repeating six near-identical branches.
 */
type ChildContainer = {
	scope: Record<string, Action>;
	setOnParent: (updated: Record<string, Action>) => Action;
};

/**
 * Enumerate every nested actions record that a control-flow parent owns
 * (If true + else, ForEach/Until body, Switch cases + default). Returns
 * an empty array for leaf actions so callers can iterate without type
 * checks. Order matters only for determinism — it's stable across calls.
 */
const getChildContainers = (parent: Action): ChildContainer[] => {
	if (isIfAction(parent)) {
		const containers: ChildContainer[] = [];
		const ifAction = parent as IfAction;
		if (ifAction.actions) {
			const inner = ifAction.actions;
			containers.push({
				scope: inner,
				setOnParent: (a) => ({ ...ifAction, actions: a }) as Action,
			});
		}
		if (ifAction.else?.actions) {
			const elseInner = ifAction.else.actions;
			containers.push({
				scope: elseInner,
				setOnParent: (a) =>
					({
						...ifAction,
						else: { ...(ifAction.else ?? {}), actions: a },
					}) as Action,
			});
		}
		return containers;
	}
	if (isForeachAction(parent) || isUntilAction(parent)) {
		const loopAction = parent as ForeachAction | UntilAction;
		if (!loopAction.actions) return [];
		return [
			{
				scope: loopAction.actions,
				setOnParent: (a) => ({ ...loopAction, actions: a }) as Action,
			},
		];
	}
	if (isSwitchAction(parent)) {
		const switchAction = parent as SwitchAction;
		const containers: ChildContainer[] = [];
		const cases = switchAction.cases ?? {};
		for (const [caseName, caseData] of Object.entries(cases)) {
			const tc = caseData as {
				case: string;
				actions?: Record<string, Action>;
			};
			if (!tc.actions) continue;
			const caseInner = tc.actions;
			containers.push({
				scope: caseInner,
				setOnParent: (a) =>
					({
						...switchAction,
						cases: { ...cases, [caseName]: { ...tc, actions: a } },
					}) as Action,
			});
		}
		if (switchAction.default?.actions) {
			const defInner = switchAction.default.actions;
			containers.push({
				scope: defInner,
				setOnParent: (a) =>
					({
						...switchAction,
						default: { ...(switchAction.default ?? {}), actions: a },
					}) as Action,
			});
		}
		return containers;
	}
	return [];
};

/**
 * Locate the actions record (scope) that owns `actionName`, returning the
 * record itself plus the rebuild function that puts an updated copy of that
 * record back into the workflow tree.
 *
 * This is what lets us mutate one scope's positions immutably without
 * deep-cloning the whole workflow. If the action lives at the top level,
 * `rebuild` writes back into `workflow.definition.actions`.
 *
 * Returns `null` when the action can't be located.
 */
export const findScopeHandle = (
	topLevelActions: Record<string, Action>,
	actionName: string,
): ScopeHandle | null => {
	// Fast path: top-level scope.
	if (actionName in topLevelActions) {
		return {
			scope: topLevelActions,
			rebuild: (updated) => updated,
		};
	}

	// Recursive descent through every nested scope. Each parent yields zero
	// or more child containers via `getChildContainers`; we check each for
	// the target name, then recurse with a `wrap` closure that composes the
	// outer rebuild chain back to the top level.
	const recurse = (
		actions: Record<string, Action>,
		wrap: (a: Record<string, Action>) => Record<string, Action>,
	): ScopeHandle | null => {
		for (const [parentName, parent] of Object.entries(actions)) {
			for (const child of getChildContainers(parent)) {
				if (actionName in child.scope) {
					return {
						scope: child.scope,
						rebuild: (updated) =>
							wrap({
								...actions,
								[parentName]: child.setOnParent(updated),
							}),
					};
				}
				const nested = recurse(child.scope, (a) =>
					wrap({ ...actions, [parentName]: child.setOnParent(a) }),
				);
				if (nested) return nested;
			}
		}
		return null;
	};

	return recurse(topLevelActions, (a) => a);
};

// ---------------------------------------------------------------------------
// Phase 1 — push downstream
// ---------------------------------------------------------------------------

/**
 * Push downstream siblings to make room for an inserted action.
 *
 * Given the top-level actions record, the **inserted** action's name, and
 * the **original target** action's name (the node whose `runAfter` was
 * rewired to depend on the inserted node), this function:
 *
 *  1. Locates the scope that contains the insertion.
 *  2. Collects the target + every transitive downstream sibling.
 *  3. Translates each of those by one slot along the flow axis.
 *  4. Returns the updated top-level actions record.
 *
 * The inserted action itself is excluded from the shift (it was placed at
 * its final slot by the caller via {@link getInsertShift}).
 *
 * No-ops cleanly when `targetName` is undefined (end-of-chain insert) or
 * when the scope isn't found, returning the same reference for cheap
 * identity comparisons.
 */
export const layoutPushDownstream = (
	topLevelActions: Record<string, Action>,
	insertedName: string,
	targetName: string | undefined,
	orientation: FlowOrientation,
): Record<string, Action> => {
	if (!targetName) return topLevelActions;

	const handle = findScopeHandle(topLevelActions, targetName);
	if (!handle) return topLevelActions;

	// Collect target + every transitive downstream sibling, then remove the
	// inserted action itself (the caller already placed it at the source-side
	// slot — don't translate it again). A single size check covers both
	// "target not in scope" (empty set) and "downstream was only the inserted
	// action" (set becomes empty after delete).
	const downstream = collectDownstreamInScope(handle.scope, targetName);
	downstream.delete(insertedName);
	if (downstream.size === 0) return topLevelActions;

	const delta = getInsertShift(orientation);
	const shifted = shiftActionPositions(handle.scope, downstream, delta);
	if (shifted === handle.scope) return topLevelActions;
	return handle.rebuild(shifted);
};

// ---------------------------------------------------------------------------
// Delete collapse (symmetric counterpart of layoutPushDownstream)
// ---------------------------------------------------------------------------

/**
 * Pull downstream siblings back when an action is removed so the gap
 * collapses instead of leaving orphan space.
 *
 * Called *before* the delete commits so the downstream set still sees the
 * doomed action in `runAfter`. The doomed action itself is excluded from
 * the shift.
 */
export const layoutAfterDelete = (
	topLevelActions: Record<string, Action>,
	deletedName: string,
	orientation: FlowOrientation,
): Record<string, Action> => {
	const handle = findScopeHandle(topLevelActions, deletedName);
	if (!handle) return topLevelActions;

	const downstream = collectDownstreamInScope(handle.scope, deletedName);
	downstream.delete(deletedName);
	if (downstream.size === 0) return topLevelActions;

	const baseDelta = getInsertShift(orientation);
	const delta = { x: -baseDelta.x, y: -baseDelta.y };
	const shifted = shiftActionPositions(handle.scope, downstream, delta);
	if (shifted === handle.scope) return topLevelActions;
	return handle.rebuild(shifted);
};

// ---------------------------------------------------------------------------
// Phase 2 — local re-space when push leaves residual overlap
// ---------------------------------------------------------------------------

/**
 * **Phase 2 escalation — local flow-axis re-space.**
 *
 * When a Phase 1 push still leaves overlap in the affected scope, run a
 * deterministic 1-D re-space across that scope's siblings **along the flow
 * axis only**. Order is preserved by walking `runAfter` topologically; any
 * cycles (which valid workflows shouldn't have) fall back to insertion order.
 *
 * This is intentionally simpler than calling the full Sugiyama engine:
 *
 *  • Synchronous — keeps `addAction` synchronous, so undo stays atomic.
 *  • Bounded — only the affected scope moves, never sibling scopes.
 *  • Predictable — linear topological order is stable across edits, so the
 *    user sees the same layout for the same structure every time.
 *
 * **Scope of the guarantee.** This pass resolves overlap *along the flow
 * axis* — the screenshot bug where downstream siblings get packed under the
 * inserted node. Cross-axis positions are preserved, so two parallel
 * siblings that already share the same cross-axis value (rare: e.g. two
 * fresh actions hand-placed at identical y in a horizontal layout) will
 * still overlap after this runs. *Format Workflow* (the full Sugiyama pass)
 * is the escape hatch for that case.
 *
 * Returns the input unchanged when no overlap was detected or the scope
 * wasn't found.
 */
export const layoutResolveOverlap = (
	topLevelActions: Record<string, Action>,
	memberActionName: string,
	orientation: FlowOrientation,
): Record<string, Action> => {
	const handle = findScopeHandle(topLevelActions, memberActionName);
	if (!handle) return topLevelActions;
	const { scope } = handle;

	const boxes = collectScopeBoxes(scope);
	if (!hasOverlap(boxes)) return topLevelActions;

	// Build the input for Kahn's algorithm: in-degree counts and a forward
	// successor map, both keyed on action name. runAfter edges go dep → action.
	// Kahn's loop below is FIFO; the cross-axis tie-break happens later when
	// `sortedNames` is sorted, not in this loop.
	const index = buildRunAfterIndex(scope);
	const inDegree = new Map<string, number>();
	const succ = new Map<string, string[]>();
	for (const name of Object.keys(scope)) inDegree.set(name, 0);
	for (const [name, deps] of index) {
		for (const dep of deps) {
			inDegree.set(name, (inDegree.get(name) ?? 0) + 1);
			const arr = succ.get(dep) ?? [];
			arr.push(name);
			succ.set(dep, arr);
		}
	}

	const positionOf = (name: string): Position | undefined => {
		const a = scope[name];
		return a ? getActionPosition(a) : undefined;
	};
	const crossAxisOf = (name: string): number => {
		const p = positionOf(name);
		if (!p) return 0;
		return orientation === "horizontal" ? p.y : p.x;
	};

	// Bucket actions by topological layer so siblings with the same depth
	// share a flow-axis slot. Within a layer, sort by cross-axis position
	// to keep vertical/horizontal pairing stable.
	const layer = new Map<string, number>();
	const queue: string[] = [];
	for (const [name, deg] of inDegree) {
		if (deg === 0) {
			layer.set(name, 0);
			queue.push(name);
		}
	}
	for (let i = 0; i < queue.length; i++) {
		const name = queue[i]!;
		const depth = layer.get(name) ?? 0;
		for (const s of succ.get(name) ?? []) {
			layer.set(s, Math.max(layer.get(s) ?? 0, depth + 1));
			const remaining = (inDegree.get(s) ?? 1) - 1;
			inDegree.set(s, remaining);
			if (remaining === 0) queue.push(s);
		}
	}
	// Unreachable nodes (only possible with a runAfter cycle inside the scope,
	// since `buildRunAfterIndex` already drops out-of-scope refs) get appended
	// to the last layer in insertion order.
	let maxLayer = 0;
	for (const v of layer.values()) maxLayer = Math.max(maxLayer, v);
	for (const name of Object.keys(scope)) {
		if (!layer.has(name)) layer.set(name, maxLayer + 1);
	}

	// Anchor: smallest flow-axis position currently used in the scope. If
	// no node has a position, anchor at 0 and let auto-layout sort it out
	// on the next pass.
	let anchor = Infinity;
	for (const name of Object.keys(scope)) {
		const p = positionOf(name);
		if (!p) continue;
		const v = orientation === "horizontal" ? p.x : p.y;
		if (v < anchor) anchor = v;
	}
	if (!Number.isFinite(anchor)) anchor = 0;

	const stride = strideFor(orientation);

	// Rebuild the scope with new positions. Object.keys() returns a fresh
	// array, so the in-place sort is safe — nobody else has a reference.
	const sortedNames = Object.keys(scope).sort((a, b) => {
		const la = layer.get(a) ?? 0;
		const lb = layer.get(b) ?? 0;
		if (la !== lb) return la - lb;
		return crossAxisOf(a) - crossAxisOf(b);
	});

	let changed = false;
	const next: Record<string, Action> = { ...scope };
	for (const name of sortedNames) {
		const action = scope[name];
		if (!action) continue;
		const oldPos = getActionPosition(action);
		if (!oldPos) continue;
		const slot = layer.get(name) ?? 0;
		const newPos: Position =
			orientation === "horizontal"
				? { x: anchor + slot * stride, y: oldPos.y }
				: { x: oldPos.x, y: anchor + slot * stride };
		if (newPos.x !== oldPos.x || newPos.y !== oldPos.y) {
			next[name] = {
				...action,
				metadata: { ...action.metadata, position: newPos },
			};
			changed = true;
		}
	}

	return changed ? handle.rebuild(next) : topLevelActions;
};

// ---------------------------------------------------------------------------
// High-level orchestrator
// ---------------------------------------------------------------------------

/**
 * Inputs to {@link layoutAfterInsert} that the store gathers from its own
 * state. Defined as an explicit shape so the orchestrator can be tested
 * without standing up a full workflow store.
 */
export type LayoutAfterInsertInput = {
	/** Actions record AFTER `addAction` has wired the new node in. */
	actions: Record<string, Action>;
	/** Workflow definition pre-update (used for `layoutMode` + trigger positions). */
	workflow: WorkflowDefinition;
	/** Insertion point passed to `addAction`. */
	insertionPoint: InsertionPointLike | undefined;
	/** Name of the newly inserted action. */
	insertedName: string;
	/** Current canvas orientation. */
	orientation: FlowOrientation;
};

/** Hoisted so the regex literal isn't re-created on every call. */
const NODE_ID_PREFIX_RE = /^(action-|trigger-)/;

/**
 * Strip the React Flow node-id prefix (`action-` / `trigger-`) and the
 * scope-path segment (`Parent/Child`) to recover the plain action name.
 * Returns `undefined` when the input is falsy.
 */
const resolveActionName = (nodeId: string | undefined): string | undefined => {
	if (!nodeId) return undefined;
	const stripped = nodeId.replace(NODE_ID_PREFIX_RE, "");
	// `split('/').pop()` returns the last segment for nested paths and the
	// whole string when there's no `/` — same result either way.
	return stripped.split("/").pop();
};

/**
 * Look up the source node's stored position, whether it's a trigger or an
 * action (possibly nested inside a scope).
 *
 * Returns `undefined` when the source can't be located or has no position.
 */
const findSourcePosition = (
	workflow: WorkflowDefinition,
	actions: Record<string, Action>,
	sourceNodeId: string | undefined,
): Position | undefined => {
	if (!sourceNodeId) return undefined;
	const leaf = resolveActionName(sourceNodeId);
	if (!leaf) return undefined;

	if (sourceNodeId.startsWith("trigger-")) {
		const trigger = workflow.definition.triggers[leaf];
		return trigger?.metadata?.position as Position | undefined;
	}

	// Action source — walk the tree to handle nested scopes.
	const handle = findScopeHandle(actions, leaf);
	if (!handle) return undefined;
	const found = handle.scope[leaf];
	return found ? getActionPosition(found) : undefined;
};

/**
 * Decide whether the caller placed the new node at a "slot" position
 * (exactly one stride past the source along the flow axis).
 *
 * Used by {@link layoutAfterInsert} to distinguish:
 *
 *  • **Slot placement** — caller called `calculateInsertionPosition` with a
 *    tight source→target gap; push needed.
 *  • **Midpoint or custom placement** — caller had a wide gap or supplied
 *    its own coordinates; no push needed.
 *
 * Returns true when we can't determine (either side missing a position),
 * matching the safer default: try the push and let `scopeHasOverlap` decide
 * whether to escalate.
 */
const isSlotPlacement = (input: LayoutAfterInsertInput): boolean => {
	const { actions, workflow, insertionPoint, insertedName, orientation } = input;
	if (!insertionPoint) return false;
	// The inserted action may live inside a nested scope (If/Foreach/Switch),
	// so look it up via `findScopeHandle` rather than indexing the top-level
	// record directly.
	const insertedHandle = findScopeHandle(actions, insertedName);
	const inserted = insertedHandle ? insertedHandle.scope[insertedName] : undefined;
	if (!inserted) return false;
	const newPos = getActionPosition(inserted);
	const sourcePos = findSourcePosition(workflow, actions, insertionPoint.sourceNodeId);
	if (!newPos || !sourcePos) return true; // Indeterminate → default to push.

	const stride = strideFor(orientation);

	// If the source→target gap was wide enough for midpoint placement, the
	// caller chose midpoint regardless of where the new node ended up. Stay
	// aligned with `calculateInsertionPosition` (which uses `gap >= 2 * stride`)
	// and skip the push. Without this guard, the boundary case `gap === 2 * stride`
	// lands the new node at exactly `source + stride` (a midpoint that
	// coincidentally matches a slot), which would otherwise trigger a push.
	const targetName = resolveActionName(insertionPoint.targetNodeId);
	if (targetName) {
		const targetHandle = findScopeHandle(actions, targetName);
		const targetAction = targetHandle ? targetHandle.scope[targetName] : undefined;
		const targetPos = targetAction ? getActionPosition(targetAction) : undefined;
		if (targetPos) {
			const gap = Math.abs(
				orientation === "horizontal"
					? targetPos.x - sourcePos.x
					: targetPos.y - sourcePos.y,
			);
			if (gap >= 2 * stride) return false;
		}
	}

	const offset =
		orientation === "horizontal"
			? Math.abs(newPos.x - sourcePos.x)
			: Math.abs(newPos.y - sourcePos.y);
	return Math.abs(offset - stride) < 1;
};

/**
 * High-level orchestrator that drives the two-phase layout after an
 * insertion. The store's `addAction` delegates here so it stays a thin
 * shell over the layout logic.
 *
 * Pipeline:
 *
 *  1. Manual mode? → return unchanged.
 *  2. End-of-chain insert (no target)? → return unchanged.
 *  3. Caller placed at midpoint (slot stride doesn't match)? → return unchanged.
 *  4. **Phase 1**: push the target + every transitive downstream sibling
 *     by one slot along the flow axis.
 *  5. **Phase 2**: if the push happened AND the scope still has overlap,
 *     re-space siblings by topological layer.
 *
 * Returns the final top-level actions record. The input reference is
 * returned unchanged when no step modified positions.
 */
export const layoutAfterInsert = (
	input: LayoutAfterInsertInput,
): Record<string, Action> => {
	const { actions, workflow, insertionPoint, insertedName, orientation } = input;

	if (getLayoutMode(workflow) === "manual") return actions;
	if (!insertionPoint) return actions;

	const resolvedTarget = resolveActionName(insertionPoint.targetNodeId);
	if (!resolvedTarget) return actions;

	// Skip the push when the caller placed the new node at a midpoint (or
	// anywhere else that isn't exactly one slot past the source). Otherwise
	// inserting into a hand-tweaked wide layout would force every
	// descendant into the slot grid, which is surprising.
	if (!isSlotPlacement({ actions, workflow, insertionPoint, insertedName, orientation })) {
		return actions;
	}

	// Phase 1.
	const pushed = layoutPushDownstream(actions, insertedName, resolvedTarget, orientation);
	const pushHappened = pushed !== actions;
	if (!pushHappened) return actions;

	// Phase 2 — only when scope still has residual overlap. Skipping when
	// it doesn't keeps the layout stable for the common case where push
	// alone was sufficient. Phase 2 is a flow-axis-only re-space; pure
	// cross-axis collisions are not handled here (see
	// `layoutResolveOverlap` docstring for the rationale and escape hatch).
	if (!scopeHasOverlap(pushed, insertedName)) return pushed;
	return layoutResolveOverlap(pushed, insertedName, orientation);
};
